import { useRef, useState } from 'react';
import { Button, Group, Modal, Text } from '@mantine/core';
import { Language } from '../../languages/languageHandler';
import { deleteEquipmentDeclaration, deleteEquipmentReason } from '../../../libraries/reason-declaration';
import { useSelector } from 'react-redux';
import { LoginState } from '../../client-redux/login/reducer';
import { AppState } from '../../client-redux/reducers';
import { StatusType } from '../../../libraries/zps';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { IconX } from '@tabler/icons-react';

interface DeleteEquipmentReasonDeclarationProps {
  language: number;
  codeReasonDeclaration: number;
  text: string;
  type: 'reason' | 'declaration';
  onReload: (type: 'reason' | 'declaration') => void;
}

const DeleteEquipmentReasonDeclaration = ({
  language,
  codeReasonDeclaration,
  type,
  onReload
}: DeleteEquipmentReasonDeclarationProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [entid] = useState<number>(loginInfo.user?.entid);
  const [code] = useState<number>(codeReasonDeclaration);
  const [langid] = useState<number>(language);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    controller.abort();
  };

  const deleteConfirmationText = type === 'reason' ? lang.REMOVE_REASON : lang.REMOVE_DECLARATION;

  const handleDelete = async () => {
    setLoading(true);

    try {
      let response;

      if (type === 'reason') {
        response = await deleteEquipmentReason(entid, code, langid);
      } else {
        response = await deleteEquipmentDeclaration(entid, code, langid);
      }

      if (!isZarphSuccessResponse(response)) {
        throw response;
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message:
          type === 'reason'
            ? lang.REMOVE_REASON_SUCCESS || 'Reason removed successfully'
            : lang.REMOVE_DECLARATION_SUCCESS || 'Declaration removed successfully',
        statusOper: response?.data?.statusOper
      });

      setTimeout(() => {
        closeModal();
        onReload(type);
      }, 300);
    } catch (error) {
      handleApiError(
        error,
        type === 'reason'
          ? lang.REMOVE_REASON_ERROR || 'Error removing reason'
          : lang.REMOVE_DECLARATION_ERROR || 'Error removing declaration'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconX stroke={2} onClick={openModal} />
      <Modal opened={showModal} onClose={closeModal} centered withCloseButton={false}>
        <Text>{deleteConfirmationText}</Text>
        <Group style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button radius="md" onClick={handleDelete} loading={loading}>
            {lang.YES}
          </Button>
          <Button radius="md" onClick={() => setShowModal(false)} style={{ marginRight: '0.5rem' }}>
            {lang.NO}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default DeleteEquipmentReasonDeclaration;
